/**
 * Story 157 — the hidden-devices list of a MOS widget instance.
 *
 * Hiding is a SUBTRACTION over the offerable set, never a whitelist: `visible = offerable − hidden`.
 * The visible set is derived and never stored (I4). The stored field is `hiddenDeviceIds`, the same
 * name in both widgets, so the rule can be implemented and tested once (P5, `03-dominio.md`).
 *
 * This module is the single place where the rule lives. Both widgets read it in consumption and
 * both settings panels write through it.
 */

const IDENTIFIER_PATTERN = /^-?\d+$/

const defaultDeviceIdOf = (device) => device?.id

/**
 * Identity of a device identifier for membership purposes: its textual form (`03-dominio.md`).
 * A `7` and a `'7'` are the same device — the configuration is serialised to text and copied
 * between templates, so an identifier that travelled cannot stop being recognised for having
 * changed representation (RN28).
 *
 * Anything that is not an identifier gets no identity, so it can never hide a device: when in
 * doubt a device is shown (P3).
 *
 * @param {*} value Raw identifier, as stored or as delivered by the source.
 * @returns {string} The canonical textual identity, or `''` when it is not an identifier.
 */
export const deviceIdKey = (value) => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : ''
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return IDENTIFIER_PATTERN.test(trimmed) ? String(Number(trimmed)) : ''
  }
  return ''
}

/**
 * Tolerant read of the stored value (RN24). Never fails: absent, null or of an unexpected type ⇒
 * nothing hidden; repeated entries ⇒ one; entries that are not identifiers ⇒ ignored. The result
 * is a set of canonical identities, in the order they were stored (the order has no meaning, I2).
 *
 * @param {*} storedValue The `hiddenDeviceIds` value as read from the widget configuration.
 * @returns {string[]} The canonical identities of the hidden devices.
 */
export const normalizeHiddenDeviceIds = (storedValue) => {
  if (!Array.isArray(storedValue)) {
    return []
  }
  const seen = new Set()
  const identities = []
  storedValue.forEach((entry) => {
    const key = deviceIdKey(entry)
    if (key === '' || seen.has(key)) {
      return
    }
    seen.add(key)
    identities.push(key)
  })
  return identities
}

/**
 * The visible set: the received collection minus the hidden devices, preserving the order and
 * every other attribute (RN7). Total by construction — it serves the Device selector's initial
 * list, a server search result (RN10) and each Device Group refresh cycle (RN17) alike. It writes
 * nothing (I6).
 *
 * Returns the very same collection when nothing is hidden, so it never invalidates a memoisation
 * that would otherwise have held (RN17, S72).
 *
 * @param {Array} devices The received collection.
 * @param {string[]} hiddenIdentities Canonical identities, from `normalizeHiddenDeviceIds`.
 * @param {Function} [deviceIdOf] Reads the identifier of an element of the collection.
 * @returns {Array} The visible collection.
 */
export const filterVisibleDevices = (devices, hiddenIdentities, deviceIdOf = defaultDeviceIdOf) => {
  if (!Array.isArray(devices)) {
    return []
  }
  if (!Array.isArray(hiddenIdentities) || hiddenIdentities.length === 0) {
    return devices
  }
  const hidden = new Set(hiddenIdentities)
  return devices.filter((device) => !hidden.has(deviceIdKey(deviceIdOf(device))))
}

/**
 * The hidden list reconciled against the offerable set: the orphans disappear from the data
 * (RN26). Writes the identifiers as the source delivers them and in the order of the offerable
 * set, so two equivalent configurations produce the same text (`03-dominio.md`).
 *
 * ⚠️ Only legitimate against an offerable set from a load that finished successfully and is not
 * empty (RN27). The caller owns that guard: this function cannot tell a failed load from a plant
 * with no devices, and reconciling against either would not be a purge but a data loss.
 *
 * @param {string[]} hiddenIdentities Canonical identities, from `normalizeHiddenDeviceIds`.
 * @param {Array} offerableDevices The reliable offerable set.
 * @param {Function} [deviceIdOf] Reads the identifier of an offerable device.
 * @returns {Array} The identifiers to store.
 */
export const reconcileHiddenDeviceIds = (hiddenIdentities, offerableDevices, deviceIdOf = defaultDeviceIdOf) => {
  if (!Array.isArray(offerableDevices) || !Array.isArray(hiddenIdentities)) {
    return []
  }
  const hidden = new Set(hiddenIdentities)
  return offerableDevices
    .filter((device) => hidden.has(deviceIdKey(deviceIdOf(device))))
    .map((device) => deviceIdOf(device))
}

/**
 * Collapses by identifier the rows of an offerable set that may carry a device more than once, so
 * the two columns of the settings panel stay a partition of it (I3, RN35). The lightweight group
 * listing returns one row per device-group association and does not deduplicate, unlike the grid.
 *
 * @param {Array} devices The offerable rows.
 * @param {Function} [deviceIdOf] Reads the identifier of a row.
 * @returns {Array} The rows, one per device, keeping the first occurrence.
 */
export const deduplicateDevicesById = (devices, deviceIdOf = defaultDeviceIdOf) => {
  if (!Array.isArray(devices)) {
    return []
  }
  const seen = new Set()
  return devices.filter((device) => {
    const key = deviceIdKey(deviceIdOf(device))
    if (key === '' || seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}
